import React, { useState, useMemo, useCallback } from "react";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import { Table, Checkbox, Button, Modal, Spin } from "antd";
import LoadingSpinner from "Components/UI/LoadingSpinner";
import { useTableSearch } from "core/Operations/useTableSearch";
import Error404 from "MainPages/ErrorPages/error404";
import { Link } from "react-router-dom";
import { all_routes } from "Router/all_routes";
import { useSelector } from "react-redux";
import { loggedInUser } from "core/redux/slice/authSlice";
import TableTop from "Components/UI/TableTop";
import { format, subMonths } from "date-fns";
import { useGetEmployeeQuery } from "AppQuries/employee/employee_list";
import Select from "react-select";
import ReportTools from "Components/UI/ReportTools";
import MonthOption from "Components/Form/MonthSelect";
import YearOption from "Components/Form/YearSelect";
import { usePayslipReportQuery } from "AppQuries/report/get-payslip-report";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import Payslip from "Components/UI/Payslip/Payslip";
// import { buildPayslipPdf } from "Components/UI/Payslip/Payslip";
import { useReGeneratePayrollMutation } from "AppQuries/payroll/generate-payroll";
import { toast } from "core/Operations/toast";
import { PDFDocument } from "pdf-lib";
import Swal from "sweetalert2";
import { RefreshCcw } from "react-feather";
import Can from "core/Operations/Can";
import { accessCodes } from "core/json/accessCode";
import { useGetCompanySettingQuery } from "AppQuries/setting/company-settings";

// ---------- helpers ----------
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const fmt = (v) => toNum(v).toFixed(2);
const numSorter = (field) => (a, b) => toNum(a[field]) - toNum(b[field]);
const strSorter = (field) => (a, b) =>
  String(a[field] ?? "").localeCompare(String(b[field] ?? ""));

// Previous month with correct year rollover (January -> December of last year)
const prevMonthDate = subMonths(new Date(), 1);

const AMOUNT_FIELDS = [
  "basicSalary",
  "addition",
  "deduction",
  "employeeCpf",
  "employerCpf",
  "totalCpf",
  "netPay",
];

const Payroll = () => {
  const userData = useSelector(loggedInUser);

  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFilterVisible, setIsFilterVisible] = useState(true);
  const [employeeCode, setEmployeeCode] = useState(0);

  const [filter, setFilter] = useState({
    month: Number(format(prevMonthDate, "M")), // 1-12
    year: format(prevMonthDate, "yyyy"),
    companyCode: userData?.companyCode,
    locationCode: userData?.selectedLocationCode,
    employeeCode: 0,
  });

  // ---------- queries ----------
  const { data: companySettings } = useGetCompanySettingQuery(
    { companyCode: userData?.companyCode },
    { enabled: !!userData?.companyCode },
  );

  // Derived value – NOT state. Calling setState during render caused an infinite re-render loop.
  const companyDetails = useMemo(
    () => ({ ...userData, ...(companySettings || {}) }),
    [userData, companySettings],
  );

  const { mutate: regeneratePayrollData, isPending: regeneratePayrollLoading } =
    useReGeneratePayrollMutation();

  const {
    data,
    isFetching,
    isError,
    error,
    refetch: getReport,
  } = usePayslipReportQuery(
    { ...filter, employeeCode },
    {
      enabled: !!filter.month && !!filter.year && !!filter.companyCode,
    },
  );

  const reportData = useMemo(
    () =>
      data?.employeeHeader?.map((d, idx) => ({
        ...d,
        id: idx + 1,
        key: idx,
        processMonth: `${format(
          new Date(d.processYear, d.procesMonth - 1, 1),
          "MMM",
        )}-${d.processYear}`,
      })) || [],
    [data],
  );

  const { data: employees } = useGetEmployeeQuery({
    companyCode: userData?.companyCode,
    locationCode: userData?.selectedLocationCode,
  });

  const employeeOptions = useMemo(
    () =>
      employees
        ?.filter((d) => d.isActive)
        .map((o) => ({
          id: o.employeeCode,
          label: `${o.uniqueId}-${o.fullName}`,
          value: o.employeeCode,
        })) || [],
    [employees],
  );

  // ---------- filter handlers ----------
  // Selection keys are row indexes, so they must be cleared whenever the data set changes.
  const monthSelectHandler = (e) => {
    setSelectedRowKeys([]);
    setFilter((prev) => ({ ...prev, month: e }));
  };

  const yearSelectHandler = (e) => {
    setSelectedRowKeys([]);
    setFilter((prev) => ({ ...prev, year: e }));
  };

  const employeeSelectHandler = (e) => {
    setSelectedRowKeys([]);
    setEmployeeCode(e?.value || 0);
  };

  const toggleFilterVisibility = () => setIsFilterVisible((v) => !v);

  // ---------- regenerate ----------
  const showWarning = (text) =>
    Swal.fire({
      title: "Warning!",
      text,
      icon: "warning",
      confirmButtonColor: "#3085d6",
      confirmButtonText: "Ok",
      customClass: { confirmButton: "btn btn-primary" },
    });

  const regeneratePayroll = (payload) => {
    const required = [
      "companyCode",
      "refNo",
      "month",
      "year",
      "userCode",
      "locationCode",
      "salaryType",
      "employeeCode",
    ];
    if (required.some((k) => !payload[k])) {
      showWarning("Something went wrong. Please try again");
      return;
    }

    Swal.fire({
      title: "Confirm to regenerate?",
      text: "Are you sure you want to regenerate payroll?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, submit it!",
      cancelButtonText: "No",
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-danger ml-1",
      },
    }).then((result) => {
      if (!result.isConfirmed) return;

      regeneratePayrollData(payload, {
        onSuccess: (response) => {
          const res = Array.isArray(response) ? response[0] : response;
          if (res?.response === true) {
            Swal.fire({
              icon: "success",
              title: "Payroll Re-Generated!",
              text: "Payroll Re-Generated Successfully.",
              customClass: { confirmButton: "btn btn-success" },
            }).then(() => {
              setSelectedRowKeys([]);
              getReport();
            });
          } else {
            showWarning(res?.errorMessage || "Failed to regenerate payroll");
          }
        },
        onError: (err) => {
          toast.error(err?.message || "Failed to regenerate payroll");
        },
      });
    });
  };

  // ---------- row selection ----------
  const handleSelect = (record, selected) => {
    setSelectedRowKeys((keys) =>
      selected ? [...keys, record.key] : keys.filter((k) => k !== record.key),
    );
  };

  const toggleSelectAll = () => {
    setSelectedRowKeys((keys) =>
      keys.length === reportData.length ? [] : reportData.map((r) => r.key),
    );
  };

  const allSelected =
    reportData.length > 0 && selectedRowKeys.length === reportData.length;

  const headerCheckbox = (
    <Checkbox
      checked={allSelected}
      indeterminate={selectedRowKeys.length > 0 && !allSelected}
      style={{ zIndex: "unset" }}
      onChange={toggleSelectAll}
    />
  );

  const rowSelection = {
    selectedRowKeys,
    type: "checkbox",
    fixed: true,
    onSelect: handleSelect,
    columnTitle: headerCheckbox,
  };

  // ---------- payslip modal ----------
  const showModal = () => setIsModalVisible(true);
  const handleCancel = () => setIsModalVisible(false);

  // ---------- PDF generation ----------
  const generatePayslipPDF = useCallback(
    async (payslipDetails) => {
      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.left = "-9999px";
      container.style.top = "0";
      document.body.appendChild(container);

      const root = createRoot(container);
      try {
        flushSync(() => {
          root.render(
            <Payslip
              payslipDetails={payslipDetails}
              companyDetails={companyDetails}
              showDownload={false}
            />,
          );
        });
        // give images/fonts a moment to load
        await new Promise((resolve) => setTimeout(resolve, 300));

        const canvas = await html2canvas(container, {
          scale: 2,
          useCORS: true,
        });
        const imgData = canvas.toDataURL("image/png", 1.0);

        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        // Fit inside the A4 page (previously tall payslips were clipped)
        const ratio = Math.min(
          pageWidth / canvas.width,
          pageHeight / canvas.height,
        );
        const w = canvas.width * ratio;
        const h = canvas.height * ratio;
        const x = (pageWidth - w) / 2;

        pdf.addImage(imgData, "PNG", x, 0, w, h, undefined, "SLOW");
        return pdf.output("arraybuffer");
      } finally {
        root.unmount(); // previously never unmounted -> memory leak
        document.body.removeChild(container);
      }
    },
    [companyDetails],
  );

  // const generatePayslipPDF = useCallback(
  //   async (payslipDetails) => {
  //     const container = document.createElement("div");
  //     container.style.position = "absolute";
  //     container.style.left = "-9999px";
  //     container.style.top = "0";
  //     container.style.width = "800px"; // fixed width = consistent layout for every payslip
  //     container.style.background = "#fff";
  //     document.body.appendChild(container);

  //     const root = createRoot(container);
  //     try {
  //       flushSync(() => {
  //         root.render(
  //           <Payslip
  //             payslipDetails={payslipDetails}
  //             companyDetails={companyDetails}
  //             showDownload={false}
  //           />,
  //         );
  //       });

  //       // buildPayslipPdf waits for fonts + images itself, no setTimeout needed
  //       const doc = await buildPayslipPdf(container, "table"); // or "image"
  //       return doc.output("arraybuffer");
  //     } finally {
  //       root.unmount();
  //       document.body.removeChild(container);
  //     }
  //   },
  //   [companyDetails],
  // );

  const mergeAndDownloadPDFs = async (pdfBuffers) => {
    const mergedPdf = await PDFDocument.create();

    for (const buffer of pdfBuffers) {
      const pdf = await PDFDocument.load(buffer);
      const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      pages.forEach((page) => mergedPdf.addPage(page));
    }

    const bytes = await mergedPdf.save();
    const blob = new Blob([bytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `All_Payslips_${filter.month}-${filter.year}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const downloadAllPayslipHandler = async () => {
    if (!selectedRowKeys.length) return;
    setIsDownloading(true);
    try {
      const pdfBuffers = [];
      for (const key of selectedRowKeys) {
        const payslipDetails = reportData[key];
        if (!payslipDetails) continue;
        pdfBuffers.push(await generatePayslipPDF(payslipDetails));
      }
      await mergeAndDownloadPDFs(pdfBuffers);
    } catch (err) {
      console.error(err);
      toast.error("Failed to download payslips");
    } finally {
      setIsDownloading(false); // previously stayed true forever on error
    }
  };

  // ---------- table ----------
  const columns = [
    {
      title: "Emp Code",
      dataIndex: "uniqueID",
      sorter: strSorter("uniqueID"),
    },
    {
      title: "Emp Name",
      dataIndex: "fullName",
      sorter: strSorter("fullName"),
      render: (text, record) => (
        <Link
          to={`${all_routes.employee}${all_routes.employee_view}/${record.employeeCode}`}
        >
          {text}
        </Link>
      ),
    },
    {
      title: "Month",
      dataIndex: "processMonth",
    },
    { title: "Salary", dataIndex: "basicSalary" },
    { title: "Addition", dataIndex: "addition" },
    { title: "Deduction", dataIndex: "deduction" },
    { title: "Emp Cpf", dataIndex: "employeeCpf" },
    { title: "Employer CPF", dataIndex: "employerCpf" },
    { title: "Total CPF", dataIndex: "totalCpf" },
    { title: "Net Pay", dataIndex: "netPay" },
    {
      title: "Action",
      dataIndex: "action",
      className: "action-table-data",
      render: (_, record) => (
        <div className="edit-delete-action">
          <Can
            accessModule={accessCodes.payroll_management}
            accessCode={accessCodes.payroll_records}
            accessType="haveEdit"
          >
            <button
              type="button"
              className="btn btn-link me-2 p-2"
              title="Regenerate payroll"
              onClick={() =>
                regeneratePayroll({
                  companyCode: record.companyCode,
                  refNo: record.refNo,
                  month: record.procesMonth?.toString(),
                  year: record.processYear?.toString(),
                  userCode: userData?.userID,
                  locationCode: record.locationCode,
                  salaryType: record.salaryType,
                  employeeCode: record.employeeCode,
                })
              }
            >
              <RefreshCcw className="feather-edit" />
            </button>
          </Can>
        </div>
      ),
    },
  ].map((col) =>
    AMOUNT_FIELDS.includes(col.dataIndex)
      ? { ...col, sorter: numSorter(col.dataIndex), render: (t) => fmt(t) }
      : col,
  );

  const searchColumns = columns.map((c) => c.dataIndex);
  const { setSearchValue, filteredData } = useTableSearch(
    reportData,
    searchColumns,
  );

  const renderSummary = () => {
    if (!filteredData?.length) return null;

    const totals = AMOUNT_FIELDS.map((field) =>
      filteredData.reduce((sum, r) => sum + toNum(r[field]), 0),
    );

    // Index 0 = selection column; 0-3 spans selection, code, name, month.
    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={4}>
          <div className="text-center">
            <b>Total</b>
          </div>
        </Table.Summary.Cell>
        {totals.map((total, i) => (
          <Table.Summary.Cell index={4 + i} key={AMOUNT_FIELDS[i]}>
            <b>{fmt(total)}</b>
          </Table.Summary.Cell>
        ))}
        <Table.Summary.Cell index={4 + totals.length} />
      </Table.Summary.Row>
    );
  };

  const filterComponents = (
    <div className="form-row row mb-1 p-1">
      <div className="col-lg-4 col-sm-6 col-12 mb-0">
        <div className="form-group">
          <label className="form-label">Employee</label>
          <Select
            className="select"
            options={employeeOptions}
            placeholder="Employee"
            value={
              employeeOptions.find((o) => o.value === employeeCode) || null
            }
            onChange={employeeSelectHandler}
            isClearable
            menuPortalTarget={document.body}
            menuPosition="absolute"
            styles={{
              control: (base) => ({ ...base, zIndex: 111 }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </div>
      </div>
      <div className="col-lg-3 col-sm-6 col-12 mb-0">
        <div className="form-group">
          <label className="form-label">Month</label>
          <MonthOption
            selectedMonth={filter.month}
            monthSelectHandler={monthSelectHandler}
          />
        </div>
      </div>
      <div className="col-lg-3 col-sm-6 col-12 mb-0">
        <div className="form-group">
          <label className="form-label">Year</label>
          <YearOption
            selectedYear={filter.year}
            yearSelectHandler={yearSelectHandler}
          />
        </div>
      </div>
      <div className="col-lg-2 col-sm-6 col-12">
        <div className="input-blocks">
          <label className="form-label">&nbsp;</label>
          <button
            type="button"
            className="btn btn-filters ms-auto"
            onClick={() => getReport()}
            disabled={isFetching}
          >
            {isFetching ? (
              <>
                <i className="fas fa-spinner fa-spin me-2" />
                Loading...
              </>
            ) : (
              <>
                <i data-feather="search" className="feather-search" />
                Filter
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  if (isFetching || regeneratePayrollLoading) return <LoadingSpinner />;
  if (isError) return <Error404 error={error} />;

  return (
    <Spin spinning={isDownloading} tip="Downloading..." size="large">
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Payroll Summary Report</h4>
                <h6>Monthly Payroll Summary Report</h6>
              </div>
            </div>

            {selectedRowKeys.length > 1 && (
              <div className="page-btn">
                <Can
                  accessModule={accessCodes.payroll_management}
                  accessCode={accessCodes.payroll_records}
                  accessType="haveAdd"
                >
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={downloadAllPayslipHandler}
                    disabled={isDownloading}
                  >
                    Download All
                  </button>
                </Can>
                &nbsp;
              </div>
            )}

            <div className="page-btn">
              <Can
                accessModule={accessCodes.payroll_management}
                accessCode={accessCodes.payroll_records}
                accessType="haveEdit"
              >
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={selectedRowKeys.length === 0}
                  onClick={showModal}
                >
                  View Payslip
                </button>
              </Can>
              &nbsp;
            </div>

            <ReportTools
              columns={columns}
              data={reportData}
              fileName={`Monthly Report ${filter.month} - ${filter.year}`}
              pdfTheme="striped"
              refresh={getReport}
            />
          </div>

          <div className="card table-list-card border-0">
            <div className="card-body">
              <TableTop
                onSearch={setSearchValue}
                toggleFilterVisibility={toggleFilterVisibility}
                isFilterVisible={isFilterVisible}
                showToggle={false}
                otherProps={filterComponents}
              />
              <div className="table-responsive">
                <Table
                  rowSelection={rowSelection}
                  columns={columns}
                  dataSource={filteredData}
                  pagination={true}
                  rowKey="key"
                  summary={renderSummary}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="Payslip Details"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="close" onClick={handleCancel}>
            Close
          </Button>,
        ]}
        width={1000}
        destroyOnClose
      >
        {selectedRowKeys.map((rowKey) =>
          reportData[rowKey] ? (
            <Payslip
              key={reportData[rowKey].employeeCode ?? rowKey}
              payslipDetails={reportData[rowKey]}
              companyDetails={companyDetails}
              showDownload={true}
            />
          ) : null,
        )}
      </Modal>
    </Spin>
  );
};

export default Payroll;
